import type { NewChat } from '../chats'
import type { Column } from '../claim-history/simple-table'

import { useCallback, useMemo } from 'react'
import { MdMenu, MdMenuOpen } from 'react-icons/md'

import { useAppDispatch, useAppSelector } from '../../store'
import * as sidebarActions from '../../store/modules/sidebar'
import { ChatReadStatus, ChatType, useChatsQuery } from '../chats'
import * as Table from '../claim-history/simple-table'
import { useTableSort } from '../claim-history/simple-table/use-sort-hook'
import { useSidebar } from '../layout/context'
import { SidebarOptionKind } from '../layout/sidebar/types'
import * as Card from '../shared/card'
import { FriendlyDateTime } from '../shared/Date/friendly-date-time'

const fromName = (r: NewChat): string => {
  if (!r.lastMessage?.author) {
    return ''
  }

  let [ resourceType, id ] = r.lastMessage.author.split('/')

  if (resourceType === 'Patient') {
    return r.patient.name
  }

  if (!r.participants) {
    return ''
  }

  if (r?.concierge) {
    return r.participants.find((p) => p.type === 'practitioner')?.name ?? 'Unknown'
  }

  let practitioner = r.participants?.find(p => p.id === id)
  return practitioner?.name ?? 'Unknown'
}

export function MyMessages (): JSX.Element {
  let userId = useAppSelector(state => state.auth?.profile?.sub)
  let sidebar = useSidebar()
  let dispatch = useAppDispatch()
  let { sort, setSort } = useTableSort<NewChat>({ field: 'date', direction: 'desc' })

  let chatsQuery = useChatsQuery({
    type: ChatType.Own,
    status: ChatReadStatus.Unread,
    userId
  })

  const columns = useMemo((): Array<Column<NewChat> & {onSort?: (a: NewChat, b: NewChat) => number}> => [
    {
      name: 'from',
      header: 'From',
      isSortable: true,
      getValue: r => (
        <button
          type='button'
          className='cursor-pointer text-blue-600 hover:text-blue-500 font-medium'
          onClick={() => {
            dispatch(sidebarActions.open(SidebarOptionKind.messages))
            dispatch(sidebarActions.selectTab(ChatType.Own))
            dispatch(sidebarActions.selectChat(r.id))
          }}
        >
          {fromName(r)}
        </button>
      ),
      onSort: (a, b) => {
        const aEl = fromName(a)
        const bEl = fromName(b)
        return aEl.localeCompare(bEl)
      }
    },
    {
      header: 'Regarding',
      name: 'regarding',
      isSortable: true,
      getValue: r => r.patient.name || 'Unknown',
      onSort: (a, b) => {
        const nameA = a.patient.name ?? 'unknown'
        const nameB = b.patient.name ?? 'unknown'
        return nameA.localeCompare(nameB)
      }
    },
    {
      name: 'date',
      header: 'Date',
      isSortable: true,
      getValue: r => (
        <button
          type='button'
          className='cursor-pointer text-blue-600 hover:text-blue-500 font-medium'
          onClick={() => {
            dispatch(sidebarActions.open(SidebarOptionKind.messages))
            dispatch(sidebarActions.selectTab(ChatType.Own))
            dispatch(sidebarActions.selectChat(r.id))
          }}
        >
          <FriendlyDateTime datetime={r.lastMessage?.createdAt ?? ''} />
        </button>
      ),
      onSort: (a, b) => {
        return a?.lastMessage?.createdAt?.localeCompare(b?.lastMessage?.createdAt ?? '') ?? 0
      }
    }
  ], [ dispatch ])

  const filtered = useMemo(() => chatsQuery.data?.pages.flat()
    .filter(chat => typeof chat?.lastMessage?.author === 'string' && chat?.lastMessage?.author !== `Practitioner/${userId ?? ''}`) ?? [], [ chatsQuery.data?.pages, userId ])

  const applyFilter = useCallback(() => {
    const sortFn = columns.find((c) => c.name === sort.field)?.onSort

    let data

    if (sortFn) {
      data = filtered?.sort(sortFn) ?? []
    } else {
      data = filtered?.sort((a, b) => {
        const aEl = (a?.[sort?.field] as string)?.toString()
        const bEl = (b?.[sort?.field] as string)?.toString()
        return aEl.localeCompare(bEl)
      }) ?? []
    }

    if (sort?.direction === 'desc') {
      data.reverse()
    }

    return data
  }, [ columns, sort.field, sort?.direction, filtered ])

  const data = applyFilter()

  return (
    <Card.Card>
      <Card.Header>
        <button
          className='flex flex-row gap-1 items-center'
          onClick={() => {
            dispatch(sidebarActions.open(SidebarOptionKind.messages))
            dispatch(sidebarActions.selectTab(ChatType.Own))
          }}
        >
          <h2>Messages</h2>
          {(!sidebar.state || sidebar.state === 'closed') && (
            <MdMenuOpen
              size={25}
              fill='#555'
              className='transition-none duration-0 translate-y-[1px]'
            />
          )}

          {sidebar.state && sidebar.state !== 'closed' && (
            <MdMenu
              size={25}
              fill='#555'
              className='transition-none duration-0 translate-y-[1px]'
            />
          )}
        </button>
      </Card.Header>
      <Table.Table
        expandable={false}
        data={data ?? []}
        error={chatsQuery.error as Error}
        fetchStatus={chatsQuery.fetchStatus}
        onSortChange={(field, direction) => setSort({ field, direction })}
        sort={sort}
        queryStatus={chatsQuery.status}
        noDataPlaceholder='There are no direct Unread Messages to display'
        columns={columns}
        disableMinHeight
      />
    </Card.Card>
  )
}
